/**
 * 规则解释器（设计文档 3.5 / F-008）。
 *
 * 对每个待分配呼叫，按优先级升序逐条评估规则；第一条全部条件命中的规则生效，
 * 执行其动作产生分配指令。无命中时不产生指令（由 rule-strategy 走兜底）。
 */
import type { Assignment, CallView, SnapshotView } from '../strategy-api';
import type { KinematicsParams } from '../../core/physics/kinematics';
import type { Action, Rule } from './rule-model';
import { evalAll, nearestIdleCar, minEtaCar } from './predicates';

export interface InterpretResult {
  assignments: Assignment[];
  matchedCallIds: Set<number>;
}

/** 同优先级按 name 字典序决胜，保证确定性。 */
function sortByPriority(rules: readonly Rule[]): Rule[] {
  return [...rules].sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name));
}

function applyAction(action: Action, call: CallView, view: SnapshotView, kin: KinematicsParams) {
  switch (action.type) {
    case 'assign-nearest':
      return nearestIdleCar(view, call);
    case 'assign-min-eta':
      return minEtaCar(view, call, kin);
  }
}

export function interpret(view: SnapshotView, rules: readonly Rule[], kin: KinematicsParams): InterpretResult {
  const sorted = sortByPriority(rules);
  const assignments: Assignment[] = [];
  const matchedCallIds = new Set<number>();
  for (const call of view.calls) {
    for (const rule of sorted) {
      if (!evalAll(rule.conditions, call)) continue;
      const car = applyAction(rule.action, call, view, kin);
      if (car) {
        assignments.push({ callId: call.id, carId: car.id });
        matchedCallIds.add(call.id);
      }
      break; // 首命中即止
    }
  }
  return { assignments, matchedCallIds };
}
